import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/CrmAuth";
import { apiError } from "@/lib/crmApi";
import { toast } from "sonner";
import { Building2, Target } from "lucide-react";
import { Lockup } from "@/crm/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Connexion réussie");
      navigate("/");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Échec de connexion");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      {/* Panneau de marque — la seule surface de l'application qui soit purement
          identitaire : elle porte l'encre, l'acier et le bronze de la charte
          « Ascension ». Le formulaire, lui, garde les couleurs d'interface. */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-[#16171a] text-white p-12 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#486079]/35 blur-3xl" />
        <div className="absolute bottom-0 -left-24 h-96 w-96 rounded-full bg-[#ac6820]/20 blur-3xl" />
        <Lockup size={40} variant="dark" className="relative" />
        <div className="relative">
          <h1 className="font-heading text-4xl font-extrabold leading-tight mb-4">
            Transformez vos données en<br /><span className="text-[#a6c1dd]">opportunités commerciales</span>.
          </h1>
          <p className="text-[#a19c93] max-w-md">
            Extraction officielle, enrichissement, scoring, attribution, pipeline et suivi commercial — le CRM de prospection B2B complet.
          </p>
          <div className="flex gap-6 mt-10 text-sm text-[#cfcbc4]">
            <div className="flex items-center gap-2"><Target size={18} className="text-[#a6c1dd]" /> Scoring intelligent</div>
            <div className="flex items-center gap-2"><Building2 size={18} className="text-[#e6a973]" /> Sources officielles</div>
          </div>
        </div>
        <div className="text-xs text-[#8b877f] relative">© 2026 ProspectLab</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <Lockup size={34} className="mb-8 lg:hidden" />
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-1">Connexion</h2>
          <p className="text-slate-500 text-sm mb-8">Accédez à votre espace commercial</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none" placeholder="vous@entreprise.fr" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Mot de passe</label>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none" placeholder="••••••••" />
            </div>
            <button data-testid="login-submit" disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg py-2.5 transition-colors disabled:opacity-60">
              {loading ? "Connexion…" : "Se connecter"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
